import { logoHorizontal } from '@/shared/assets'
import { LogoutIcon, Span } from '@/components/atoms'
import { LanguageSwitcher } from '../language-switcher'
import { MobileMenu } from '../mobile-menu'
import { ProfileMenu } from '../profile-menu'
import { useTranslation } from 'react-i18next'
import { useAppHeader } from './app-header.hook'

type AppHeaderVariant = 'app' | 'auth'

interface AppHeaderProps {
  /** 'app' (default) requires auth and shows the logout control; 'auth' renders on
   * public auth pages (login / verify-code) without the logout control. */
  variant?: AppHeaderVariant
}

export function AppHeader({ variant = 'app' }: AppHeaderProps) {
  const {
    isAuthenticated,
    logout,
    loading,
    supportPhone,
    showProfileMenu,
    userName,
  } = useAppHeader()
  const { t } = useTranslation()
  const isAuthVariant = variant === 'auth'
  if (!isAuthVariant && !isAuthenticated) return

  return (
    <header
      className={`sticky top-0 right-0 left-0 z-[var(--z-header)] border-b border-gray-200 bg-white ${
        isAuthVariant
          ? 'h-[var(--header-h)]'
          : 'h-[calc(var(--header-h)+var(--sat))] pt-[var(--sat)]'
      }`}
    >
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-4 md:px-[4rem]">
        <img
          src={logoHorizontal}
          alt="Kanguro"
          className="h-[2.215rem] max-w-26.25"
        />

        {isAuthVariant ? (
          <div className="flex items-center gap-4 md:gap-[2.62rem]">
            {supportPhone && (
              <a
                href={`tel:${supportPhone}`}
                className="hidden text-black transition-colors hover:text-gray-900 md:inline"
              >
                {supportPhone}
              </a>
            )}
            <LanguageSwitcher variant="dropdown" />
          </div>
        ) : (
          <>
            {/* Desktop nav */}
            <div className="hidden items-center gap-[2.62rem] md:flex">
              {supportPhone && (
                <a
                  href={`tel:${supportPhone}`}
                  className="text-black transition-colors hover:text-gray-900"
                >
                  {supportPhone}
                </a>
              )}
              <LanguageSwitcher variant="dropdown" />
              {showProfileMenu ? (
                <ProfileMenu
                  name={userName}
                  onLogout={logout}
                  isLoggingOut={loading}
                />
              ) : (
                <button
                  onClick={logout}
                  disabled={loading}
                  aria-label="Logout"
                  data-testid="logout-button"
                  className="flex items-center gap-2 text-base text-black transition-colors hover:text-gray-900"
                  title={t('home.logout')}
                >
                  <Span>{t('home.logout')}</Span>
                  <LogoutIcon className="text-indigo-base h-6 w-6" />
                </button>
              )}
            </div>

            <MobileMenu showProfileIcon={showProfileMenu} />
          </>
        )}
      </div>
    </header>
  )
}
